import React, { useState, useRef } from 'react';
import { Camera, X, Image, Loader2, WifiOff, RefreshCw, Cloud, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Converte um File para base64 data URL
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Comprime imagem para reduzir tamanho do base64 no IndexedDB
 */
const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(blob);
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível processar a imagem.'));
    };
    img.src = url;
  });
};

/**
 * Verifica se uma URL é uma foto salva offline (base64)
 */
export const isOfflinePhoto = (url) => {
  return url && url.startsWith('data:image/');
};

/**
 * Faz upload de uma foto base64 para o Cloudinary (usado na sincronização)
 * Lança erro com `.cloudinaryLimit = true` quando o motivo for limite/quota da conta.
 */
export const uploadOfflinePhoto = async (base64Data, folder = 'horimetros') => {
  const sigResponse = await fetch(`${API_URL}/api/cloudinary/signature?folder=${folder}`);
  if (!sigResponse.ok) throw new Error('Erro ao obter assinatura do Cloudinary');
  const sigData = await sigResponse.json();

  // Converter base64 para blob
  const res = await fetch(base64Data);
  const blob = await res.blob();
  const file = new File([blob], 'offline_photo.jpg', { type: 'image/jpeg' });

  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', sigData.api_key);
  formData.append('timestamp', sigData.timestamp);
  formData.append('signature', sigData.signature);
  formData.append('folder', sigData.folder);

  const uploadResponse = await fetch(
    `https://api.cloudinary.com/v1_1/${sigData.cloud_name}/image/upload`,
    { method: 'POST', body: formData }
  );

  if (!uploadResponse.ok) {
    // Tenta extrair o motivo do erro retornado pelo Cloudinary
    let motivo = '';
    try { motivo = (await uploadResponse.json())?.error?.message || ''; } catch (_e) { /* ignore */ }
    const limite = uploadResponse.status === 420 || uploadResponse.status === 429 ||
      /rate limit|too many requests|quota|credit|limit reached|exceeded|usage/i.test(motivo);
    const err = new Error(
      limite
        ? 'Limite da conta Cloudinary atingido. Avise o gestor para verificar a conta (plano/uso).'
        : (motivo || `Falha no upload (HTTP ${uploadResponse.status}).`)
    );
    err.cloudinaryLimit = limite;
    err.status = uploadResponse.status;
    throw err;
  }
  const uploadData = await uploadResponse.json();
  return uploadData.secure_url.replace('/upload/', '/upload/w_800,q_auto,f_auto/');
};

export default function ImageUpload({ 
  value, 
  onChange, 
  label = "Foto do Horímetro",
  folder = "horimetros",
  disabled = false 
}) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || null);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState(null);
  const fileInputRef = useRef(null);

  // Mantém o preview em sincronia quando o valor muda externamente (ex.: sync automático)
  React.useEffect(() => { setPreview(value || null); }, [value]);

  const handleResend = async () => {
    if (!preview || !isOfflinePhoto(preview)) return;
    setResending(true);
    setResendError(null);
    try {
      const url = await uploadOfflinePhoto(preview, folder);
      setPreview(url);
      onChange(url);
      toast.success('Foto reenviada e confirmada no servidor!');
    } catch (error) {
      setResendError(error.message || 'Falha ao reenviar a foto.');
      if (error.cloudinaryLimit) {
        toast.error('Limite da conta Cloudinary atingido. Avise o gestor.');
      } else {
        toast.error(error.message || 'Falha ao reenviar a foto.');
      }
      // Regra de Ouro: NUNCA descartar a foto local em caso de falha.
    } finally {
      setResending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo: 5MB');
      return;
    }

    setUploading(true);

    try {
      if (!navigator.onLine) {
        // OFFLINE: comprimir e salvar como base64
        const compressed = await compressImage(file);
        const base64 = await fileToBase64(compressed);
        
        setPreview(base64);
        onChange(base64);
        toast.success('Foto salva localmente. Será enviada ao reconectar.');
      } else {
        // ONLINE: upload normal para Cloudinary
        const sigResponse = await fetch(`${API_URL}/api/cloudinary/signature?folder=${folder}`);
        if (!sigResponse.ok) throw new Error('Erro ao obter assinatura');
        const sigData = await sigResponse.json();

        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', sigData.api_key);
        formData.append('timestamp', sigData.timestamp);
        formData.append('signature', sigData.signature);
        formData.append('folder', sigData.folder);

        const uploadResponse = await fetch(
          `https://api.cloudinary.com/v1_1/${sigData.cloud_name}/image/upload`,
          { method: 'POST', body: formData }
        );

        if (!uploadResponse.ok) throw new Error('Erro no upload');
        const uploadData = await uploadResponse.json();

        const optimizedUrl = uploadData.secure_url.replace(
          '/upload/',
          '/upload/w_800,q_auto,f_auto/'
        );

        setPreview(optimizedUrl);
        onChange(optimizedUrl);
        toast.success('Foto enviada com sucesso!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      // Fallback: se falhar online, salvar como base64
      try {
        const compressed = await compressImage(file);
        const base64 = await fileToBase64(compressed);
        setPreview(base64);
        onChange(base64);
        toast.warning(
          navigator.onLine
            ? 'Falha no envio ao servidor. Foto salva localmente — use "Reenviar".'
            : 'Sem conexão. Foto salva localmente.'
        );
      } catch (fallbackError) {
        toast.error('Erro ao salvar foto. Tente novamente.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isOffline = preview && isOfflinePhoto(preview);

  return (
    <div className="space-y-2">
      <label className="block text-sm text-gray-400">{label}</label>
      
      {preview ? (
        <div className="relative">
          <img 
            src={preview} 
            alt="Foto do horímetro" 
            className="w-full h-48 object-cover rounded-lg border border-gray-700"
          />
          {isOffline && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-orange-500/90 text-white text-xs px-2 py-1 rounded" data-testid="offline-photo-badge">
              <WifiOff size={12} />
              {resending ? 'Sincronizando...' : 'Salva localmente'}
            </div>
          )}
          {!isOffline && preview && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-green-600/90 text-white text-xs px-2 py-1 rounded" data-testid="photo-confirmed-badge">
              <Cloud size={12} /> Confirmada no servidor
            </div>
          )}
          {isOffline && !disabled && (
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              data-testid="resend-photo-button"
              className="absolute bottom-2 right-2 flex items-center gap-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-xs px-2 py-1 rounded transition-colors"
              title="Reenviar esta foto ao servidor (Cloudinary)"
            >
              {resending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {resending ? 'Enviando...' : 'Reenviar'}
            </button>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
            >
              <X size={16} />
            </button>
          )}
          {resendError && (
            <div className="absolute top-2 left-2 right-12 flex items-start gap-1 bg-red-600/95 text-white text-[11px] px-2 py-1 rounded" data-testid="resend-photo-error">
              <AlertTriangle size={12} className="shrink-0 mt-0.5" />
              <span>{resendError}</span>
            </div>
          )}
        </div>
      ) : (
        <div 
          className={`
            border-2 border-dashed border-gray-700 rounded-lg p-6
            flex flex-col items-center justify-center gap-3
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-yellow-500 cursor-pointer'}
            transition-colors
          `}
          onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
        >
          {uploading ? (
            <>
              <Loader2 size={32} className="text-yellow-500 animate-spin" />
              <span className="text-gray-400 text-sm">
                {navigator.onLine ? 'Enviando...' : 'Salvando localmente...'}
              </span>
            </>
          ) : (
            <>
              <div className="flex gap-4">
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <Camera size={24} className="text-yellow-500" />
                </div>
                <div className="p-3 bg-gray-800 rounded-lg">
                  <Image size={24} className="text-gray-400" />
                </div>
              </div>
              <div className="text-center">
                <p className="text-gray-300 text-sm">Tirar foto ou selecionar</p>
                <p className="text-gray-500 text-xs mt-1">JPG, PNG ou WebP até 5MB</p>
                {!navigator.onLine && (
                  <p className="text-orange-400 text-xs mt-1 flex items-center justify-center gap-1">
                    <WifiOff size={10} /> Será salva localmente
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || uploading}
      />
    </div>
  );
}
